import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { supabase } from "services/supabase";
import moment from "moment";

// ------------------------------------------------------------------------
// Initial state
const initialState = {
  subscription: null,
  loading: false,
  error: null,
  subscriptionLoaded: false,
};

// ------------------------------------------------------------------------
// Selectors
export const selectSubscriptionLoaded = (state) =>
  state.subscriptionReducer.subscriptionLoaded;
export const selectSubscription = (state) =>
  state.subscriptionReducer.subscription;
export const selectTierName = (state) =>
  state.subscriptionReducer.subscription?.tier;

// ------------------------------------------------------------------------
// Async Thunks

// Fetch user's subscription by user ID
export const fetchUserSubscription = createAsyncThunk(
  "subscription/fetchUserSubscription",
  async ({ userId, ...params } = {}, { rejectWithValue }) => {
    try {
      if (!userId) {
        console.error("fetchUserSubscription: User not authenticated");
        throw new Error("User not authenticated");
      }

      // Build Supabase query to get subscription by user ID
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("uid", userId)
        .single();

      if (error) {
        // If no subscription found, that's okay - return null
        if (error.code === "PGRST116") {
          return null;
        }
        throw error;
      }

      // Check for free tier and determine if user is in trial mode
      if (data?.tier === "free") {
        if (data?.created_at && data?.trial_duration) {
          const createdAt = moment(data.created_at);
          const now = moment();
          const daysSinceCreated = now.diff(createdAt, "days");
          const daysLeft = data.trial_duration - daysSinceCreated;
          data.trial_ends_at = createdAt
            .add(data.trial_duration, "days")
            .toISOString();
          data.trial_mode = daysLeft > 0;
          data.trial_days_left = daysLeft > 0 ? daysLeft : 0;
          data.tier_display_name = "Trial";
          data.limits = {
            clients: 10,
            // clients: 5,
            calendars: 1,
            invoices: "basic",
            analytics: "basic",
            reporting: "basic",
            dashboards: "basic",
          };
        } else {
          // Free tier but missing trial data - trial has ended or never started
          data.trial_days_left = 0;
          data.trial_mode = false;
        }
      } else {
        switch (data?.tier) {
          case "starter":
            data.tier_display_name = "The Starter";
            data.limits = {
              clients: 10,
              calendars: 1,
              invoices: "basic",
              analytics: "basic",
              reporting: "basic",
              dashboards: "basic",
            };
            break;
          case "advanced":
            data.tier_display_name = "The Scaling Fractional";
            data.limits = {
              clients: 100,
              calendars: 3,
              invoices: "auto",
              analytics: "advanced",
              reporting: "advanced",
              dashboards: "advanced",
            };
            break;
          case "enterprise":
            data.tier_display_name = "The All-In Fractional";
            data.limits = {
              clients: 10000,
              calendars: 10000,
              invoices: "auto",
              analytics: "enterprise",
              reporting: "enterprise",
              dashboards: "enterprise",
              bankIntegration: "enterprise",
            };
            break;
        }
      }

      // Term expiry: if kind=term and endAt is past, apply free-tier limits
      // Also compute term_days_left for header badge when endAt is in future
      if (data?.kind === "term") {
        const endAtRaw = data?.endAt ?? data?.end_at ?? null;
        if (endAtRaw) {
          const endAt = moment(endAtRaw);
          const now = moment();
          if (!endAt.isAfter(now)) {
            data.limits = {
              clients: 10,
              calendars: 1,
              invoices: "basic",
              analytics: "basic",
              reporting: "basic",
              dashboards: "basic",
            };
            data.term_days_left = 0;
          } else {
            data.term_days_left = Math.max(0, endAt.diff(now, "days"));
            data.term_end_at = endAt.toISOString();
          }
        }
      }

      // limits_override: merge DB overrides onto tier defaults (null/{} = no overrides)
      const override = data?.limits_override ?? data?.limitsOverride ?? null;
      if (override && typeof override === "object" && Object.keys(override).length > 0 && data.limits) {
        if (override.clients != null) data.limits.clients = Number(override.clients);
        if (override.calendars != null) data.limits.calendars = Number(override.calendars);
      }

      console.log("fetchUserSubscription: data", data);
      return data;
    } catch (error) {
      console.error("fetchUserSubscription: Caught error", error);
      return rejectWithValue(error.message || error.toString());
    }
  }
);

// Update user's subscription tier
export const updateSubscriptionTier = createAsyncThunk(
  "subscription/updateSubscriptionTier",
  async ({ userId, tier }, { rejectWithValue }) => {
    try {
      if (!userId) {
        console.error("updateSubscriptionTier: User not authenticated");
        throw new Error("User not authenticated");
      }

      // Check if subscription exists
      const { data: existingSubscription, error: fetchError } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("uid", userId)
        .single();

      let result;

      if (fetchError && fetchError.code === "PGRST116") {
        // No subscription exists, create a new one
        const { data, error } = await supabase
          .from("subscriptions")
          .insert({
            uid: userId,
            tier,
            created_at: moment().toISOString(),
          })
          .select()
          .single();

        if (error) {
          console.error(
            "updateSubscriptionTier: Error creating subscription",
            error
          );
          throw error;
        }

        result = data;
      } else if (fetchError) {
        // Some other error occurred
        console.error(
          "updateSubscriptionTier: Error fetching existing subscription",
          fetchError
        );
        throw fetchError;
      } else {
        // Subscription exists, update it
        const { data, error } = await supabase
          .from("subscriptions")
          .update({
            tier,
            updated_at: moment().toISOString(),
          })
          .eq("uid", userId)
          .select()
          .single();

        if (error) {
          console.error(
            "updateSubscriptionTier: Error updating subscription",
            error
          );
          throw error;
        }

        result = data;
      }

      return result;
    } catch (error) {
      console.error("updateSubscriptionTier: Caught error", error);
      return rejectWithValue(error.message || error.toString());
    }
  }
);

// ------------------------------------------------------------------------
// Slice

export const SubscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearSubscription: (state) => {
      state.subscription = null;
      state.subscriptionLoaded = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user subscription
      .addCase(fetchUserSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserSubscription.fulfilled, (state, action) => {
        state.loading = false;
        // Store the full subscription object, not just the tier string
        // Ensure trial_days_left is always a number
        const subscription = action.payload || { tier: "free" };
        if (subscription.tier === "free" && (subscription.trial_days_left === undefined || subscription.trial_days_left === null)) {
          subscription.trial_days_left = 0;
        }
        state.subscription = subscription;
        state.subscriptionLoaded = true;
        state.error = null;
      })
      .addCase(fetchUserSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.subscription = null;
        state.subscriptionLoaded = false;
      })
      // Update subscription tier
      .addCase(updateSubscriptionTier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSubscriptionTier.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure trial_days_left is always a number for free tier
        const subscription = action.payload;
        if (subscription?.tier === "free" && (subscription.trial_days_left === undefined || subscription.trial_days_left === null)) {
          subscription.trial_days_left = 0;
        }
        state.subscription = subscription;
        state.subscriptionLoaded = true;
        state.error = null;
      })
      .addCase(updateSubscriptionTier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export actions and reducers
export const { setLoading, setError, clearSubscription } =
  SubscriptionSlice.actions;

export default SubscriptionSlice.reducer;
